import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/api";

type TabType = "incident" | "maintenance" | "email";
type SortBy = "newest" | "oldest" | "title";

interface IncidentTemplate {
  id: string;
  title: string;
  details: string;
  severity: string;
  status: string;
  is_silent: boolean;
  labels: string[];
  created_at: string;
}

interface MaintenanceTemplate {
  id: string;
  title: string;
  details: string;
  duration_minutes: number;
  is_silent: boolean;
  labels: string[];
  created_at: string;
}

interface NotificationTemplate {
  id: string;
  event_type: string;
  event_type_display: string;
  subject: string;
  html_body: string;
  text_body: string;
  slack_body: string;
  teams_body: string;
  is_active: boolean;
  updated_at: string;
  created_at: string;
}

const EVENT_TYPE_OPTIONS = [
  { value: "status_change", label: "Status change" },
  { value: "incident_created", label: "Incident created" },
  { value: "incident_resolved", label: "Incident resolved" },
  { value: "maintenance_starting", label: "Maintenance starting" },
  { value: "maintenance_completed", label: "Maintenance completed" },
  { value: "subscription_confirm", label: "Subscription confirmation" },
];

const PLACEHOLDER_HELP: Record<string, { vars: string[]; note?: string }> = {
  status_change: {
    vars: [
      "{service_name}", "{component_name}", "{region_name}",
      "{new_status}", "{previous_status}", "{reason}",
      "{triggered_by}", "{occurred_at}", "{unsubscribe_url}",
    ],
  },
  incident_created: {
    vars: ["{title}", "{severity}", "{unsubscribe_url}"],
  },
  incident_resolved: {
    vars: ["{title}", "{unsubscribe_url}"],
  },
  maintenance_starting: {
    vars: [
      "{title}", "{description}", "{starts_at}", "{ends_at}",
      "{affected_services}", "{affected_components}", "{service_count}", "{created_by}", "{unsubscribe_url}",
    ],
    note: "{affected_services} = 'ServiceName — Region' pairs. {affected_components} = component names only (deduplicated). Datetimes are formatted as 'YYYY-MM-DD HH:MM:SS IST / YYYY-MM-DD HH:MM:SS EST'.",
  },
  maintenance_completed: {
    vars: [
      "{title}", "{description}", "{starts_at}", "{ends_at}",
      "{affected_services}", "{operational_components}", "{degraded_components}",
      "{service_count}", "{all_clear}", "{created_by}", "{unsubscribe_url}",
    ],
    note: "{affected_services} = 'ServiceName — Region' pairs. {operational_components} = components back to operational. {degraded_components} = components still down. {all_clear} = 'Yes' or 'No'. Datetimes include IST and EST.",
  },
  subscription_confirm: {
    vars: ["{confirm_url}", "{contact}"],
  },
};

function StatusBadge({ v }: { v: string }) {
  return <span className={`ac-badge ac-badge--${v}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>;
}

export function TemplatesPage() {
  const [tab, setTab] = useState<TabType>("incident");
  const [incidentTemplates, setIncidentTemplates] = useState<IncidentTemplate[]>([]);
  const [maintenanceTemplates, setMaintenanceTemplates] = useState<MaintenanceTemplate[]>([]);
  const [notificationTemplates, setNotificationTemplates] = useState<NotificationTemplate[]>([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<(IncidentTemplate | MaintenanceTemplate | NotificationTemplate) | null>(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Form state — shared
  const [fTitle, setFTitle] = useState("");
  const [fDetails, setFDetails] = useState("");
  const [fSeverity, setFSeverity] = useState("minor");
  const [fStatus, setFStatus] = useState("active");
  const [fSilent, setFSilent] = useState(false);
  const [fLabels, setFLabels] = useState("");
  const [fDuration, setFDuration] = useState(60);
  // Form state — email notification templates
  const [fEventType, setFEventType] = useState("status_change");
  const [fSubject, setFSubject] = useState("");
  const [fHtmlBody, setFHtmlBody] = useState("");
  const [fTextBody, setFTextBody] = useState("");
  const [fSlackBody, setFSlackBody] = useState("");
  const [fTeamsBody, setFTeamsBody] = useState("");
  const [fIsActive, setFIsActive] = useState(true);

  async function loadIncidentTemplates() {
    const r = await adminFetch("/api/v1/admin/templates/incidents/");
    if (r.ok) setIncidentTemplates(await r.json());
  }

  async function loadMaintenanceTemplates() {
    const r = await adminFetch("/api/v1/admin/templates/maintenance/");
    if (r.ok) setMaintenanceTemplates(await r.json());
  }

  async function loadNotificationTemplates() {
    const r = await adminFetch("/api/v1/admin/notification-templates/");
    if (r.ok) setNotificationTemplates(await r.json());
  }

  useEffect(() => {
    loadIncidentTemplates();
    loadMaintenanceTemplates();
    loadNotificationTemplates();
  }, []);

  function resetForm() {
    setFTitle("");
    setFDetails("");
    setFSeverity("minor");
    setFStatus("active");
    setFSilent(false);
    setFLabels("");
    setFDuration(60);
    setFEventType("status_change");
    setFSubject("");
    setFHtmlBody("");
    setFTextBody("");
    setFSlackBody("");
    setFTeamsBody("");
    setFIsActive(true);
    setError("");
  }

  function openCreate() {
    resetForm();
    setEditTarget(null);
    setShowCreate(true);
  }

  function openEdit(t: IncidentTemplate | MaintenanceTemplate | NotificationTemplate) {
    if (tab === "email") {
      const nt = t as NotificationTemplate;
      setFEventType(nt.event_type);
      setFSubject(nt.subject);
      setFHtmlBody(nt.html_body);
      setFTextBody(nt.text_body);
      setFSlackBody(nt.slack_body || "");
      setFTeamsBody(nt.teams_body || "");
      setFIsActive(nt.is_active);
    } else {
      const it = t as IncidentTemplate | MaintenanceTemplate;
      setFTitle(it.title);
      setFDetails(it.details);
      setFSilent(it.is_silent);
      setFLabels((it.labels || []).join(", "));
      if ("severity" in it) {
        setFSeverity((it as IncidentTemplate).severity);
        setFStatus((it as IncidentTemplate).status);
      }
      if ("duration_minutes" in it) {
        setFDuration((it as MaintenanceTemplate).duration_minutes);
      }
    }
    setError("");
    setEditTarget(t);
    setShowCreate(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      if (tab === "email") {
        if (!fSubject.trim()) { setError("Subject is required."); setSubmitting(false); return; }
        if (!fHtmlBody.trim()) { setError("HTML body is required."); setSubmitting(false); return; }
        const isEdit = editTarget !== null;
        const url = isEdit
          ? `/api/v1/admin/notification-templates/${(editTarget as NotificationTemplate).id}/`
          : "/api/v1/admin/notification-templates/";
        const body = {
          event_type: fEventType,
          subject: fSubject.trim(),
          html_body: fHtmlBody,
          text_body: fTextBody,
          slack_body: fSlackBody,
          teams_body: fTeamsBody,
          is_active: fIsActive,
        };
        const r = await adminFetch(url, {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.event_type || d.subject || d.detail || `HTTP ${r.status}`);
        }
        setShowCreate(false);
        resetForm();
        await loadNotificationTemplates();
      } else {
        if (!fTitle.trim()) { setError("Title is required."); setSubmitting(false); return; }
        const labelsArr = fLabels.split(",").map((l) => l.trim()).filter(Boolean);
        const isEdit = editTarget !== null;
        const baseUrl = tab === "incident"
          ? "/api/v1/admin/templates/incidents/"
          : "/api/v1/admin/templates/maintenance/";
        const body: Record<string, unknown> = {
          title: fTitle.trim(),
          details: fDetails,
          is_silent: fSilent,
          labels: labelsArr,
        };
        if (tab === "incident") {
          body.severity = fSeverity;
          body.status = fStatus;
        } else {
          body.duration_minutes = fDuration;
        }
        const url = isEdit ? `${baseUrl}${(editTarget as IncidentTemplate).id}/` : baseUrl;
        const r = await adminFetch(url, {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.title || d.detail || `HTTP ${r.status}`);
        }
        setShowCreate(false);
        resetForm();
        if (tab === "incident") await loadIncidentTemplates();
        else await loadMaintenanceTemplates();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this template? This cannot be undone.")) return;
    if (tab === "email") {
      const r = await adminFetch(`/api/v1/admin/notification-templates/${id}/`, { method: "DELETE" });
      if (r.ok) await loadNotificationTemplates();
      return;
    }
    const baseUrl = tab === "incident"
      ? "/api/v1/admin/templates/incidents/"
      : "/api/v1/admin/templates/maintenance/";
    const r = await adminFetch(`${baseUrl}${id}/`, { method: "DELETE" });
    if (r.ok) {
      if (tab === "incident") await loadIncidentTemplates();
      else await loadMaintenanceTemplates();
    }
  }

  // Filter and sort
  function sortItems<T extends { title: string; created_at: string }>(items: T[]): T[] {
    const sorted = [...items];
    if (sortBy === "newest") sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
    if (sortBy === "oldest") sorted.sort((a, b) => a.created_at.localeCompare(b.created_at));
    if (sortBy === "title") sorted.sort((a, b) => a.title.localeCompare(b.title));
    return sorted;
  }

  const filteredIncident = sortItems(
    incidentTemplates.filter((t) => {
      if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterStatus !== "All" && t.status.toLowerCase() !== filterStatus.toLowerCase()) return false;
      return true;
    })
  );

  const filteredMaintenance = sortItems(
    maintenanceTemplates.filter((t) => {
      if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
  );

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Templates</h1>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--primary" onClick={openCreate}>
            {tab === "incident" ? "New incident template" : tab === "maintenance" ? "New maintenance template" : "New email template"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="ac-tabs">
        {(["incident", "maintenance", "email"] as TabType[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`ac-tab-btn${tab === t ? " ac-tab-btn--active" : ""}`}
            onClick={() => { setTab(t); setSearch(""); setFilterStatus("All"); }}
          >
            {{ incident: "Incident templates", maintenance: "Maintenance templates", email: "Email templates" }[t]}
            <span style={{ marginLeft: "0.375rem", fontSize: "0.75rem", background: "var(--aptean-bg-overlay)", borderRadius: "9999px", padding: "0.05rem 0.4rem" }}>
              {{ incident: incidentTemplates.length, maintenance: maintenanceTemplates.length, email: notificationTemplates.length }[t]}
            </span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="ac-filter-bar" style={{ gap: "var(--aptean-space-3)" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
          <input
            type="text"
            className="aptean-input"
            placeholder="Search templates"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {tab !== "email" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "0.875rem", color: "var(--aptean-text-secondary)", whiteSpace: "nowrap" }}>Sort by</span>
            <select
              className="aptean-input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              style={{ width: 140 }}
              aria-label="Sort by"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">Title</option>
            </select>
          </div>
        )}
        {tab === "incident" && (
          <select
            className="aptean-input"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ width: 120 }}
            aria-label="Filter by status"
          >
            <option value="All">All</option>
            <option value="active">Active</option>
            <option value="investigating">Investigating</option>
            <option value="identified">Identified</option>
            <option value="monitoring">Monitoring</option>
            <option value="resolved">Resolved</option>
          </select>
        )}
      </div>

      {/* Table — Email Templates */}
      {tab === "email" && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "22%" }}>Event type</th>
                <th style={{ width: "30%" }}>Subject</th>
                <th style={{ width: "30%" }}>HTML body (preview)</th>
                <th>Active</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {notificationTemplates.filter((t) =>
                !search || t.event_type_display.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase())
              ).length === 0 ? (
                <tr><td colSpan={5}>
                  <div className="ac-empty">
                    <div className="ac-empty__title">No email templates</div>
                    <div className="ac-empty__body">Create one per event type to customise notification emails sent via SendGrid.</div>
                  </div>
                </td></tr>
              ) : (
                notificationTemplates
                  .filter((t) => !search || t.event_type_display.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase()))
                  .map((t) => (
                    <tr key={t.id}>
                      <td><div className="ac-row-title">{t.event_type_display}</div></td>
                      <td className="ac-td--muted" style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</td>
                      <td className="ac-td--muted" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.html_body.replace(/<[^>]+>/g, " ").slice(0, 120)}</td>
                      <td>
                        <span className={`ac-badge ac-badge--${t.is_active ? "active" : "resolved"}`}>{t.is_active ? "Yes" : "No"}</span>
                      </td>
                      <td className="ac-td--actions ac-td--right">
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                          <button type="button" className="aptean-btn aptean-btn--ghost"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => openEdit(t)}>&#9998;</button>
                          <button type="button" className="aptean-btn aptean-btn--danger"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => handleDelete(t.id)}>&#128465;</button>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Table — Incident / Maintenance Templates */}
      {tab !== "email" && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "25%" }}>Title</th>
                <th style={{ width: "30%" }}>Details</th>
                <th>Status</th>
                <th>Silent</th>
                <th>Labels</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tab === "incident" ? (
                filteredIncident.length === 0 ? (
                  <tr><td colSpan={6}>
                    <div className="ac-empty">
                      <div className="ac-empty__title">No incident templates</div>
                      <div className="ac-empty__body">Create one to quickly file incidents.</div>
                    </div>
                  </td></tr>
                ) : (
                  filteredIncident.map((t) => (
                    <tr key={t.id}>
                      <td><div className="ac-row-title">{t.title}</div></td>
                      <td className="ac-td--muted" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.details || "—"}</td>
                      <td><StatusBadge v={t.status} /></td>
                      <td className="ac-td--muted">{t.is_silent ? "Yes" : "No"}</td>
                      <td className="ac-td--muted">{t.labels?.join(", ") || "N/A"}</td>
                      <td className="ac-td--actions ac-td--right">
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                          <button type="button" className="aptean-btn aptean-btn--ghost"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => openEdit(t)}>&#9998;</button>
                          <button type="button" className="aptean-btn aptean-btn--danger"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => handleDelete(t.id)}>&#128465;</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                filteredMaintenance.length === 0 ? (
                  <tr><td colSpan={6}>
                    <div className="ac-empty">
                      <div className="ac-empty__title">No maintenance templates</div>
                      <div className="ac-empty__body">Create one to quickly schedule maintenance.</div>
                    </div>
                  </td></tr>
                ) : (
                  filteredMaintenance.map((t) => (
                    <tr key={t.id}>
                      <td><div className="ac-row-title">{t.title}</div></td>
                      <td className="ac-td--muted" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.details || "—"}</td>
                      <td><span className="ac-badge">{t.duration_minutes} min</span></td>
                      <td className="ac-td--muted">{t.is_silent ? "Yes" : "No"}</td>
                      <td className="ac-td--muted">{t.labels?.join(", ") || "N/A"}</td>
                      <td className="ac-td--actions ac-td--right">
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                          <button type="button" className="aptean-btn aptean-btn--ghost"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => openEdit(t)}>&#9998;</button>
                          <button type="button" className="aptean-btn aptean-btn--danger"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => handleDelete(t.id)}>&#128465;</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {showCreate && (
        <div className="ac-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog" style={{ maxWidth: tab === "email" ? 720 : undefined, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">
                {editTarget ? "Edit" : "New"} {tab === "incident" ? "Incident" : tab === "maintenance" ? "Maintenance" : "Email"} Template
              </h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowCreate(false)} aria-label="Close">&#10005;</button>
            </div>
            <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div className="ac-dialog__body" style={{ overflowY: "auto", flex: 1 }}>
                {error && <div className="ac-alert ac-alert--error">{error}</div>}

                {tab === "email" ? (
                  <>
                    <div className="ac-field">
                      <label htmlFor="tpl-event-type">Event type *</label>
                      <select id="tpl-event-type" className="aptean-input"
                        value={fEventType}
                        onChange={(e) => setFEventType(e.target.value)}
                        disabled={!!editTarget}
                      >
                        {EVENT_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <small className="ac-field__help" style={{ color: "var(--aptean-color-text-secondary)", fontSize: "0.75rem" }}>
                        {(() => {
                          const help = PLACEHOLDER_HELP[fEventType];
                          if (!help) return null;
                          return (
                            <span>
                              <strong style={{ color: "var(--aptean-text-primary)" }}>Available placeholders:</strong>
                              <span style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.375rem" }}>
                                {help.vars.map((v) => (
                                  <code key={v} style={{
                                    background: "var(--aptean-bg-overlay)",
                                    borderRadius: "4px",
                                    padding: "0.1rem 0.4rem",
                                    fontSize: "0.7rem",
                                    color: "var(--aptean-teal, #54B3BE)",
                                    cursor: "default",
                                  }}>{v}</code>
                                ))}
                              </span>
                              {help.note && (
                                <span style={{ display: "block", marginTop: "0.375rem", color: "var(--aptean-text-secondary)" }}>
                                  {help.note}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </small>
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-subject">Subject *</label>
                      <input id="tpl-subject" type="text" className="aptean-input"
                        value={fSubject} onChange={(e) => setFSubject(e.target.value)}
                        placeholder="e.g. [StatusCentral] {service_name} is now {status}" />
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-html-body">HTML body *</label>
                      <textarea id="tpl-html-body" className="aptean-input" rows={10}
                        value={fHtmlBody} onChange={(e) => setFHtmlBody(e.target.value)}
                        placeholder="<p>Hello,</p><p>The status of <strong>{service_name}</strong> changed to <strong>{status}</strong>.</p><p><a href='{unsubscribe_url}'>Unsubscribe</a></p>"
                        style={{ fontFamily: "monospace", fontSize: "0.8rem" }} />
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-text-body">Plain-text body <span style={{ fontWeight: 400, color: "var(--aptean-color-text-secondary)" }}>(optional — auto-generated from HTML if blank)</span></label>
                      <textarea id="tpl-text-body" className="aptean-input" rows={4}
                        value={fTextBody} onChange={(e) => setFTextBody(e.target.value)}
                        placeholder="Leave blank to auto-strip HTML" />
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-slack-body">Slack body (mrkdwn) <span style={{ fontWeight: 400, color: "var(--aptean-color-text-secondary)" }}>(optional — falls back to plain-text body if blank)</span></label>
                      <textarea id="tpl-slack-body" className="aptean-input" rows={4}
                        value={fSlackBody} onChange={(e) => setFSlackBody(e.target.value)}
                        placeholder="*{service_name}* is now {status}. <{unsubscribe_url}|Unsubscribe>" />
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-teams-body">Microsoft Teams body <span style={{ fontWeight: 400, color: "var(--aptean-color-text-secondary)" }}>(optional — falls back to plain-text body if blank)</span></label>
                      <textarea id="tpl-teams-body" className="aptean-input" rows={4}
                        value={fTeamsBody} onChange={(e) => setFTeamsBody(e.target.value)}
                        placeholder="{service_name} is now {status}." />
                    </div>
                    <div className="ac-field">
                      <label className="subscribe-checkbox">
                        <input type="checkbox" checked={fIsActive}
                          onChange={(e) => setFIsActive(e.target.checked)} />
                        Active (use this template for outgoing notifications)
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="ac-field">
                      <label htmlFor="tpl-title">Title *</label>
                      <input id="tpl-title" type="text" className="aptean-input"
                        value={fTitle} onChange={(e) => setFTitle(e.target.value)} required />
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-details">Details</label>
                      <textarea id="tpl-details" className="aptean-input" rows={4}
                        value={fDetails} onChange={(e) => setFDetails(e.target.value)}
                        placeholder="Template details / message body" />
                    </div>
                    {tab === "incident" && (
                      <>
                        <div className="ac-field">
                          <label htmlFor="tpl-severity">Severity</label>
                          <select id="tpl-severity" className="aptean-input"
                            value={fSeverity} onChange={(e) => setFSeverity(e.target.value)}>
                            <option value="minor">Minor</option>
                            <option value="major">Major</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                        <div className="ac-field">
                          <label htmlFor="tpl-status">Status</label>
                          <select id="tpl-status" className="aptean-input"
                            value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                            <option value="active">Active</option>
                            <option value="investigating">Investigating</option>
                            <option value="identified">Identified</option>
                            <option value="monitoring">Monitoring</option>
                            <option value="resolved">Resolved</option>
                          </select>
                        </div>
                      </>
                    )}
                    {tab === "maintenance" && (
                      <div className="ac-field">
                        <label htmlFor="tpl-duration">Duration (minutes)</label>
                        <input id="tpl-duration" type="number" className="aptean-input" min={1}
                          value={fDuration} onChange={(e) => setFDuration(Number(e.target.value))} />
                      </div>
                    )}
                    <div className="ac-field">
                      <label className="subscribe-checkbox">
                        <input type="checkbox" checked={fSilent}
                          onChange={(e) => setFSilent(e.target.checked)} />
                        Silent (do not send notifications)
                      </label>
                    </div>
                    <div className="ac-field">
                      <label htmlFor="tpl-labels">Labels (comma-separated)</label>
                      <input id="tpl-labels" type="text" className="aptean-input"
                        value={fLabels} onChange={(e) => setFLabels(e.target.value)}
                        placeholder="e.g. critical, security" />
                    </div>
                  </>
                )}
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>
                  {submitting ? "Saving..." : editTarget ? "Save Changes" : "Create Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
